import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faTv,
  faTimes,
  faArrowUpRightFromSquare,
  faRotate,
  faVolumeHigh,
  faVolumeXmark,
  faCompress,
  faExpand,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import { Link, useLocation } from 'react-router-dom';
import { embedSrc, useLiveStream } from '../contexts/LiveStreamContext';

function PlayerSurface({ compact = false }) {
  const {
    active,
    muted,
    playerNonce,
    refreshPlayer,
    videoRef,
    scheduleStallRefresh,
    clearStallRefresh,
  } = useLiveStream();

  const isFile = active?.type === 'file';
  const playerKey = active ? `${active.type}-${active.source}-${muted ? 'm' : 'u'}-${playerNonce}` : 'none';
  const playerSrc = useMemo(() => {
    if (!active || isFile) return '';
    const src = embedSrc(active, muted);
    const joiner = src.includes('?') ? '&' : '?';
    return `${src}${joiner}cb=${playerNonce}`;
  }, [active, isFile, playerNonce, muted]);

  if (!active) {
    return (
      <div className={`px-3 ${compact ? 'py-6' : 'py-8'} text-center text-sm text-muted`}>
        Add a stream URL on the dashboard to start watching.
      </div>
    );
  }

  return (
    <div className={`relative w-full bg-black ${compact ? 'aspect-video' : 'aspect-video'}`}>
      {isFile ? (
        <video
          key={playerKey}
          ref={videoRef}
          className="absolute inset-0 w-full h-full"
          src={active.source}
          controls
          autoPlay
          muted={muted}
          playsInline
          onError={() => setTimeout(refreshPlayer, 1500)}
          onStalled={scheduleStallRefresh}
          onWaiting={scheduleStallRefresh}
          onPlaying={clearStallRefresh}
          onCanPlay={clearStallRefresh}
        />
      ) : (
        <iframe
          key={playerKey}
          title={active.label}
          src={playerSrc}
          className="absolute inset-0 w-full h-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      )}
    </div>
  );
}

/** Full desk card (dashboard). */
export function LiveStreamCard() {
  const {
    streams,
    active,
    activeId,
    setActiveId,
    muted,
    toggleMuted,
    autoRefresh,
    setAutoRefresh,
    pipEnabled,
    togglePipEnabled,
    refreshPlayer,
    addStream,
    removeStream,
  } = useLiveStream();

  const [showAdd, setShowAdd] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [labelInput, setLabelInput] = useState('');
  const [error, setError] = useState('');

  const isYouTube = active?.type === 'youtube' || active?.type === 'youtube-channel';

  const handleAdd = (e) => {
    e.preventDefault();
    const result = addStream(urlInput, labelInput);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setUrlInput('');
    setLabelInput('');
    setError('');
    setShowAdd(false);
  };

  return (
    <div className="lt-card overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-hairline">
        <div className="flex items-center gap-2 min-w-0">
          <FontAwesomeIcon icon={faTv} className="text-xs text-muted" />
          <span className="lt-label">Live video</span>
          {active && <span className="text-xs text-ink truncate">{active.label}</span>}
        </div>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          {active && (
            <button
              type="button"
              onClick={toggleMuted}
              className={`h-8 px-2 inline-flex items-center rounded-md text-xs font-medium ${
                muted
                  ? 'border border-hairline text-muted hover:bg-elevated hover:text-ink'
                  : 'bg-action text-white'
              }`}
              title={muted ? 'Sound is off — click to unmute' : 'Sound is on — click to mute'}
            >
              <FontAwesomeIcon icon={muted ? faVolumeXmark : faVolumeHigh} className="mr-1" />
              {muted ? 'Muted' : 'Sound'}
            </button>
          )}
          {active && (
            <button
              type="button"
              onClick={refreshPlayer}
              className="h-8 px-2 inline-flex items-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink text-xs font-medium"
              title="Reload the player"
            >
              <FontAwesomeIcon icon={faRotate} className="mr-1" />
              Reload
            </button>
          )}
          <button
            type="button"
            onClick={() => setAutoRefresh((v) => !v)}
            className={`h-8 px-2 inline-flex items-center rounded-md text-xs font-medium ${
              autoRefresh
                ? 'bg-action text-white'
                : 'border border-hairline text-muted hover:bg-elevated hover:text-ink'
            }`}
            title={autoRefresh ? 'Auto-reload on (every 6 min, and when the tab wakes up)' : 'Auto-reload off'}
          >
            Auto
          </button>
          <button
            type="button"
            onClick={togglePipEnabled}
            className={`h-8 px-2 inline-flex items-center rounded-md text-xs font-medium ${
              pipEnabled
                ? 'bg-action text-white'
                : 'border border-hairline text-muted hover:bg-elevated hover:text-ink'
            }`}
            title={
              pipEnabled
                ? 'TV overlay on — keeps playing in a corner when you leave the dashboard'
                : 'TV overlay off — player only shows on the dashboard'
            }
          >
            Overlay
          </button>
          {active?.url && (
            <a
              href={active.url}
              target="_blank"
              rel="noopener noreferrer"
              className="h-8 px-2 inline-flex items-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink text-xs font-medium"
              title="Open original stream"
            >
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="mr-1" />
              Open
            </a>
          )}
          <button
            type="button"
            onClick={() => {
              setShowAdd((v) => !v);
              setError('');
            }}
            className="h-8 px-2 rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink text-xs font-medium"
          >
            <FontAwesomeIcon icon={showAdd ? faTimes : faPlus} className="mr-1" />
            {showAdd ? 'Close' : 'Add'}
          </button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} className="px-3 py-3 border-b border-hairline space-y-2 bg-canvas">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="YouTube, Twitch, Vimeo, .m3u8/.mp4, or any embed URL"
            className="w-full h-9 px-2.5 text-xs lt-input"
          />
          <div className="flex gap-2">
            <input
              type="text"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              placeholder="Label (optional)"
              className="flex-1 h-9 px-2.5 text-xs lt-input"
            />
            <button
              type="submit"
              className="h-9 px-3 rounded-md bg-action text-white text-xs font-semibold"
            >
              Save
            </button>
          </div>
          {error && <div className="text-[11px] text-bear">{error}</div>}
        </form>
      )}

      {streams.length > 0 && (
        <div className="flex gap-1 px-3 py-2 overflow-x-auto border-b border-hairline">
          {streams.map((stream) => (
            <div key={stream.id} className="flex items-center flex-shrink-0">
              <button
                type="button"
                onClick={() => setActiveId(stream.id)}
                className={`h-7 px-2 rounded-l-md text-[11px] font-medium ${
                  stream.id === activeId
                    ? 'bg-elevated text-ink'
                    : 'text-muted hover:bg-elevated hover:text-ink'
                }`}
              >
                {stream.label}
              </button>
              <button
                type="button"
                onClick={() => removeStream(stream.id)}
                className="h-7 w-7 rounded-r-md text-muted hover:text-bear hover:bg-elevated"
                title="Remove"
              >
                <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
              </button>
            </div>
          ))}
        </div>
      )}

      <PlayerSurface />

      {isYouTube && active && (
        <div className="px-3 py-2 text-[11px] text-muted border-t border-hairline">
          YouTube often blocks embedded live video with a bot check.{' '}
          <a
            href={active.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-action hover:underline"
          >
            Open it in a new tab
          </a>{' '}
          instead, or add a Twitch / direct stream URL.
        </div>
      )}
    </div>
  );
}

/** Bottom-corner overlay when away from the dashboard. */
export function LiveStreamPip() {
  const location = useLocation();
  const {
    active,
    streams,
    activeId,
    setActiveId,
    muted,
    toggleMuted,
    pipEnabled,
    setPipEnabled,
    pipExpanded,
    setPipExpanded,
    refreshPlayer,
  } = useLiveStream();

  const onDashboard = location.pathname === '/';
  const isLogin = location.pathname === '/login';
  const isAlgoPopout =
    location.pathname === '/algorithm' &&
    new URLSearchParams(location.search).get('popout') === '1';

  if (!pipEnabled || onDashboard || isLogin || isAlgoPopout || !active) {
    return null;
  }

  return (
    <div
      className={`fixed z-[55] left-3 sm:left-4 bottom-3 sm:bottom-4 w-[min(100vw-1.5rem,22rem)] lt-card overflow-hidden shadow-ticket ${
        pipExpanded ? '' : 'w-auto'
      }`}
    >
      <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 border-b border-hairline bg-surface">
        <div className="flex items-center gap-1.5 min-w-0">
          <FontAwesomeIcon icon={faTv} className="text-[10px] text-action shrink-0" />
          <span className="text-[11px] font-medium text-ink truncate">{active.label}</span>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={toggleMuted}
            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted hover:bg-elevated hover:text-ink"
            title={muted ? 'Unmute' : 'Mute'}
          >
            <FontAwesomeIcon icon={muted ? faVolumeXmark : faVolumeHigh} className="text-[11px]" />
          </button>
          <button
            type="button"
            onClick={refreshPlayer}
            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted hover:bg-elevated hover:text-ink"
            title="Reload"
          >
            <FontAwesomeIcon icon={faRotate} className="text-[11px]" />
          </button>
          <button
            type="button"
            onClick={() => setPipExpanded((v) => !v)}
            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted hover:bg-elevated hover:text-ink"
            title={pipExpanded ? 'Minimize' : 'Expand'}
          >
            <FontAwesomeIcon icon={pipExpanded ? faCompress : faExpand} className="text-[11px]" />
          </button>
          <Link
            to="/"
            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted hover:bg-elevated hover:text-ink"
            title="Open dashboard"
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px]" />
          </Link>
          <button
            type="button"
            onClick={() => setPipEnabled(false)}
            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted hover:bg-elevated hover:text-bear"
            title="Turn off TV overlay"
          >
            <FontAwesomeIcon icon={faTimes} className="text-[11px]" />
          </button>
        </div>
      </div>

      {pipExpanded && (
        <>
          {streams.length > 1 && (
            <div className="flex gap-1 px-2 py-1.5 overflow-x-auto border-b border-hairline">
              {streams.map((stream) => (
                <button
                  key={stream.id}
                  type="button"
                  onClick={() => setActiveId(stream.id)}
                  className={`h-6 px-2 rounded-md text-[10px] font-medium whitespace-nowrap ${
                    stream.id === activeId
                      ? 'bg-elevated text-ink'
                      : 'text-muted hover:bg-elevated hover:text-ink'
                  }`}
                >
                  {stream.label}
                </button>
              ))}
            </div>
          )}
          <PlayerSurface compact />
        </>
      )}
    </div>
  );
}

/** @deprecated Prefer LiveStreamCard — kept for existing imports */
const LiveStream = LiveStreamCard;

export default LiveStream;
